import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Divider, Grid, Stack, Typography } from '@mui/material';
import { motion, useInView } from 'framer-motion';
import { APP_FONT_FAMILY } from '../../theme/appTypography';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell,
  Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

// ── Brand tokens ──────────────────────────────────────────────────────────────
const G = {
  50:  '#EAF7EC',
  100: '#CFEDD3',
  300: '#6BC576',
  500: '#269D32',
  600: '#1F7E29',
  700: '#0e6346',
  800: '#0A4A35',
};
const INK = { 900: '#14171A', 700: '#4A5159', 400: '#B5BCC4', 200: '#E8EBEF', 100: '#F3F5F7' };

const STATUS_META = {
  cold:      { label: 'Cold',      color: '#4A6B8E', bg: '#EDF2F7' },
  warm:      { label: 'Warm',      color: '#C68A2E', bg: '#FBF4E8' },
  hot:       { label: 'Hot',       color: '#C4624A', bg: '#FBEFEC' },
  urgent:    { label: 'Urgent',    color: '#8A3F2C', bg: '#F9E5E0' },
  inactive:  { label: 'Inactive',  color: '#8E96A0', bg: '#F3F5F7' },
  converted: { label: 'Converted', color: '#269D32', bg: '#EAF7EC' },
};
const SENTIMENT_COLORS = { positive: '#269D32', neutral: '#4A6B8E', negative: '#C4624A', mixed: '#C68A2E' };
const QUALITY_COLORS   = { high: '#269D32', medium: '#4A6B8E', low: '#C68A2E', spam_like: '#C4624A' };
const DEST_GRADIENT    = ['#0e6346', '#269D32', '#6BC576', '#CFEDD3', '#EAF7EC'];

// ── Helpers ────────────────────────────────────────────────────────────────────

function useCountUp(target, duration = 900, enabled = true) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!enabled || !target) { setVal(target || 0); return; }
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(target * ease));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, enabled]);
  return val;
}

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

const fadeUp = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };

// ── Chart tooltip ─────────────────────────────────────────────────────────────
function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{ bgcolor: '#fff', border: `1px solid ${INK[200]}`, borderRadius: 1.5, px: 1.5, py: 1, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
      {label && <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>{label}</Typography>}
      {payload.map((p, i) => (
        <Typography key={i} variant="body2" fontWeight={600} sx={{ color: p.color || INK[900] }}>
          {p.name ? `${p.name}: ` : ''}{Number(p.value).toLocaleString()}
        </Typography>
      ))}
    </Box>
  );
}

// ── Panel shell ────────────────────────────────────────────────────────────────
function Panel({ title, subtitle, children, accent }) {
  return (
    <Box
      sx={{
        borderRadius: 3,
        border: `1px solid ${INK[200]}`,
        bgcolor: '#fff',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      {(title || subtitle) && (
        <Box sx={{ px: 2.5, pt: 2, pb: 1.5, borderBottom: `1px solid ${INK[100]}` }}>
          {accent && (
            <Box sx={{ width: 3, height: 16, borderRadius: 2, bgcolor: accent, display: 'inline-block', mr: 1, mb: '-3px' }} />
          )}
          <Typography variant="subtitle2" fontWeight={600} color={INK[900]} display="inline">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
              {subtitle}
            </Typography>
          )}
        </Box>
      )}
      <Box sx={{ p: 2.5 }}>{children}</Box>
    </Box>
  );
}

// ── Animated stat card ─────────────────────────────────────────────────────────
function StatCard({ label, rawValue, suffix = '', prefix = '', sub, accent = G[500], isReady }) {
  const counted = useCountUp(safeNum(rawValue), 900, isReady);
  const display = rawValue == null ? '–' : `${prefix}${counted.toLocaleString()}${suffix}`;

  return (
    <motion.div variants={fadeUp} style={{ flex: '1 1 160px', minWidth: 140 }}>
      <Box
        sx={{
          borderRadius: 3,
          border: `1px solid ${INK[200]}`,
          bgcolor: '#fff',
          px: 2.5,
          py: 2,
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0,
            height: 3,
            bgcolor: accent,
            borderRadius: '3px 3px 0 0',
          },
        }}
      >
        <Typography
          sx={{
            fontFamily: APP_FONT_FAMILY,
            fontWeight: 500,
            fontSize: 30,
            lineHeight: 1.1,
            color: accent,
            letterSpacing: '-0.02em',
          }}
        >
          {display}
        </Typography>
        <Typography variant="body2" fontWeight={600} color={INK[900]} mt={0.5}>
          {label}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary">
            {sub}
          </Typography>
        )}
      </Box>
    </motion.div>
  );
}

// ── Animated horizontal bar ───────────────────────────────────────────────────
function HBar({ label, value, max, color, count, isReady }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Typography variant="body2" fontWeight={500} color={INK[700]}>{label}</Typography>
        <Typography variant="body2" fontWeight={600} color={INK[900]}>{count.toLocaleString()}</Typography>
      </Stack>
      <Box sx={{ height: 8, borderRadius: 99, bgcolor: INK[100], overflow: 'hidden' }}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: isReady ? `${pct}%` : 0 }}
          transition={{ duration: 0.7, ease: 'easeOut', delay: 0.1 }}
          style={{ height: '100%', borderRadius: 99, background: color }}
        />
      </Box>
    </Box>
  );
}

// ── Status funnel row ─────────────────────────────────────────────────────────
function FunnelRow({ statusDist, total, isReady }) {
  const ORDER = ['cold', 'inactive', 'warm', 'hot', 'urgent', 'converted'];
  const byStatus = Object.fromEntries(statusDist.map((s) => [s.status, s.count]));

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch', flexWrap: 'wrap' }}>
      {ORDER.map((key, i) => {
        const count = safeNum(byStatus[key]);
        const pct = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
        const meta = STATUS_META[key] || { label: key, color: '#9e9e9e', bg: '#f5f5f5' };
        return (
          <motion.div
            key={key}
            variants={fadeUp}
            style={{ flex: '1 1 100px', minWidth: 90 }}
          >
            <Box
              sx={{
                borderRadius: 2.5,
                bgcolor: meta.bg,
                border: `1px solid ${meta.color}22`,
                px: 1.75,
                py: 1.5,
                height: '100%',
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: meta.color,
                  mb: 1,
                }}
              />
              <Typography
                sx={{
                  fontFamily: APP_FONT_FAMILY,
                  fontSize: 22,
                  fontWeight: 500,
                  color: meta.color,
                  lineHeight: 1,
                }}
              >
                {isReady ? count.toLocaleString() : '—'}
              </Typography>
              <Typography variant="caption" fontWeight={600} color={INK[700]} display="block" mt={0.5}>
                {meta.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {pct}% of total
              </Typography>
            </Box>
          </motion.div>
        );
      })}
    </Box>
  );
}

// ── Donut with centre label ───────────────────────────────────────────────────
function DonutChart({ data, colorMap, nameKey, valueKey = 'count', centreLabel, centreValue }) {
  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie
          data={data}
          dataKey={valueKey}
          nameKey={nameKey}
          cx="50%"
          cy="50%"
          innerRadius={62}
          outerRadius={90}
          paddingAngle={2}
          isAnimationActive
          animationBegin={100}
          animationDuration={900}
          label={false}
        >
          {data.map((entry) => (
            <Cell key={entry[nameKey]} fill={colorMap[entry[nameKey]] || '#9e9e9e'} />
          ))}
        </Pie>
        <Tooltip content={<ChartTip />} />
        {/* Centre text via foreignObject workaround using absolute overlay */}
      </PieChart>
    </ResponsiveContainer>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function InsightsDashboard({ data, loading }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2, mb: 3 }}>
        <CircularProgress size={20} thickness={3} sx={{ color: G[500] }} />
        <Typography variant="body2" color="text.secondary">Loading intelligence snapshot…</Typography>
      </Box>
    );
  }

  if (!data) return null;

  const {
    summary = {},
    status_distribution = [],
    readiness_buckets = [],
    destination_rankings = [],
    top_tags = [],
    sentiment_distribution = [],
    engagement_quality = [],
    weekly_classifications = [],
    campaign_performance = {},
    weekly_plan_summary = {},
  } = data;

  const total = safeNum(summary.total_profiles);
  const hotCount = safeNum(summary.hot_and_urgent);
  const warmCount = safeNum(summary.warm_count);
  const avgReady = safeNum(summary.avg_readiness);
  const classified = safeNum(summary.classifications_this_week);

  // Derived metrics
  const convertedEntry = status_distribution.find((s) => s.status === 'converted');
  const convertedCount = safeNum(convertedEntry?.count);
  const conversionRate = total > 0 ? ((convertedCount / total) * 100).toFixed(1) : 0;


  const positiveSentiment = safeNum(sentiment_distribution.find((s) => s.sentiment === 'positive')?.count);
  const negativeSentiment = safeNum(sentiment_distribution.find((s) => s.sentiment === 'negative')?.count);
  const totalSentiment = sentiment_distribution.reduce((acc, s) => acc + safeNum(s.count), 0);
  const engagementHealth = totalSentiment > 0
    ? Math.round(((positiveSentiment - negativeSentiment * 0.5) / totalSentiment) * 100)
    : null;

  const maxReadiness = Math.max(...readiness_buckets.map((b) => safeNum(b.count)), 1);
  const maxDest = Math.max(...destination_rankings.map((d) => safeNum(d.count)), 1);
  const maxTag  = Math.max(...top_tags.map((t) => safeNum(t.count)), 1);

  // Color-fill destinations with gradient
  const destWithColor = destination_rankings.slice(0, 8).map((d, i) => ({
    ...d,
    fill: DEST_GRADIENT[Math.min(i, DEST_GRADIENT.length - 1)],
  }));

  // Tag colours cycle
  const TAG_POOL = [G[700], G[500], G[300], '#4A6B8E', '#C68A2E', '#C4624A'];
  const tagsWithColor = top_tags.slice(0, 8).map((t, i) => ({
    ...t,
    fill: TAG_POOL[i % TAG_POOL.length],
  }));

  // Weekly area data — smooth the labels
  const areaData = weekly_classifications.map((d) => ({
    ...d,
    label: d.date ? d.date.slice(5) : d.date, // show MM-DD
  }));

  const statusColorMap = Object.fromEntries(
    Object.entries(STATUS_META).map(([k, v]) => [k, v.color])
  );

  return (
    <motion.div ref={ref} variants={stagger} initial="hidden" animate={inView ? 'show' : 'hidden'} style={{ width: '100%' }}>

      {/* ── Stat row ── */}
      <motion.div variants={stagger}>
        <Stack direction="row" spacing={1.5} mb={3} flexWrap="wrap" useFlexGap>
          <StatCard label="Profiled contacts"   rawValue={total}       sub="ready for campaigns"    accent={G[700]}      isReady={inView} />
          <StatCard label="Hot contacts"        rawValue={hotCount}    sub="high buying readiness"  accent="#C4624A"     isReady={inView} />
          <StatCard label="Warm contacts"       rawValue={warmCount}   sub="nurture opportunity"    accent="#C68A2E"     isReady={inView} />
          <StatCard label="Avg readiness"       rawValue={avgReady}    suffix="/100"                accent={G[500]}      isReady={inView} />
          <StatCard label="Converted"           rawValue={convertedCount} sub={`${conversionRate}% conversion rate`} accent={G[500]} isReady={inView} />
          <StatCard label="Classified this week" rawValue={classified}  sub="new inbound emails"    accent="#4A6B8E"     isReady={inView} />
        </Stack>
      </motion.div>

      <Divider sx={{ mb: 3, borderColor: INK[100] }} />

      {/* ── Contact journey funnel ── */}
      <motion.div variants={fadeUp}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} color={INK[700]} mb={1.5}
            sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.1em' }}>
            Contact journey
          </Typography>
          {status_distribution.length > 0 ? (
            <motion.div variants={stagger}>
              <FunnelRow statusDist={status_distribution} total={total} isReady={inView} />
            </motion.div>
          ) : (
            <Typography variant="body2" color="text.secondary">No status data yet.</Typography>
          )}
        </Box>
      </motion.div>

      <Divider sx={{ mb: 3, borderColor: INK[100] }} />

      {/* ── Row 1: Status distribution | Buying readiness | Top destinations | Top tags ── */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={3}>
          <motion.div variants={fadeUp} style={{ height: '100%' }}>
            <Panel title="Status distribution" accent={G[500]}>
              {status_distribution.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No data yet.</Typography>
              ) : (
                <>
                  <DonutChart
                    data={status_distribution}
                    colorMap={statusColorMap}
                    nameKey="status"
                  />
                  <Stack direction="row" flexWrap="wrap" gap={1} mt={1} justifyContent="center">
                    {status_distribution.map((s) => {
                      const meta = STATUS_META[s.status] || { label: s.status, color: '#9e9e9e' };
                      const pct = total > 0 ? ((safeNum(s.count) / total) * 100).toFixed(0) : 0;
                      return (
                        <Stack key={s.status} direction="row" alignItems="center" spacing={0.5}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: meta.color, flexShrink: 0 }} />
                          <Typography variant="caption" color="text.secondary">
                            {meta.label} <strong style={{ color: INK[900] }}>({pct}%)</strong>
                          </Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                </>
              )}
            </Panel>
          </motion.div>
        </Grid>

        <Grid item xs={12} md={3}>
          <motion.div variants={fadeUp} style={{ height: '100%' }}>
            <Panel title="Buying readiness" subtitle="Contact count by readiness score bucket" accent={G[500]}>
              {readiness_buckets.every((b) => safeNum(b.count) === 0) ? (
                <Typography variant="body2" color="text.secondary">No data yet.</Typography>
              ) : (
                <Box sx={{ pt: 1 }}>
                  {readiness_buckets.map((b) => {
                    const gradient = `linear-gradient(90deg, ${G[700]}, ${G[300]})`;
                    return (
                      <HBar
                        key={b.range}
                        label={b.range}
                        value={safeNum(b.count)}
                        max={maxReadiness}
                        color={gradient}
                        count={safeNum(b.count)}
                        isReady={inView}
                      />
                    );
                  })}
                  <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${INK[100]}` }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">Avg score</Typography>
                      <Typography variant="caption" fontWeight={700} color={G[700]}>{avgReady}/100</Typography>
                    </Stack>
                    <Box sx={{ height: 6, borderRadius: 99, bgcolor: INK[100], overflow: 'hidden', mt: 0.75 }}>
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: inView ? `${avgReady}%` : 0 }}
                        transition={{ duration: 1, ease: 'easeOut', delay: 0.3 }}
                        style={{ height: '100%', borderRadius: 99, background: `linear-gradient(90deg, ${G[700]}, ${G[300]})` }}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </Panel>
          </motion.div>
        </Grid>

        <Grid item xs={12} md={3}>
          <motion.div variants={fadeUp} style={{ height: '100%' }}>
            <Panel title="Top destination interests" subtitle="Most requested travel destinations" accent={G[700]}>
              {destination_rankings.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No data yet.</Typography>
              ) : (
                <Box sx={{ pt: 0.5 }}>
                  {destination_rankings.slice(0, 6).map((d, i) => (
                    <HBar
                      key={d.destination}
                      label={d.destination}
                      value={safeNum(d.count)}
                      max={maxDest}
                      color={DEST_GRADIENT[Math.min(i, DEST_GRADIENT.length - 1)]}
                      count={safeNum(d.count)}
                      isReady={inView}
                    />
                  ))}
                </Box>
              )}
            </Panel>
          </motion.div>
        </Grid>

        <Grid item xs={12} md={3}>
          <motion.div variants={fadeUp} style={{ height: '100%' }}>
            <Panel title="Top behavioural tags" subtitle="Most common contact activity signals" accent="#4A6B8E">
              {top_tags.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No data yet.</Typography>
              ) : (
                <Box sx={{ pt: 0.5 }}>
                  {top_tags.slice(0, 6).map((t, i) => (
                    <HBar
                      key={t.tag}
                      label={t.tag}
                      value={safeNum(t.count)}
                      max={maxTag}
                      color={TAG_POOL[i % TAG_POOL.length]}
                      count={safeNum(t.count)}
                      isReady={inView}
                    />
                  ))}
                </Box>
              )}
            </Panel>
          </motion.div>
        </Grid>
      </Grid>

      {/* ── Row 2: Sentiment | Engagement quality | Audience health | Contact pipeline ── */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={3}>
          <motion.div variants={fadeUp} style={{ height: '100%' }}>
            <Panel title="Sentiment (last 30 days)" subtitle="Tone of inbound emails" accent={G[500]}>
              {sentiment_distribution.length === 0 ? (
                <Box sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Appears as inbound emails are classified.
                  </Typography>
                </Box>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie
                        data={sentiment_distribution}
                        dataKey="count"
                        nameKey="sentiment"
                        cx="50%"
                        cy="50%"
                        innerRadius={48}
                        outerRadius={72}
                        paddingAngle={3}
                        isAnimationActive
                        animationBegin={200}
                        animationDuration={900}
                      >
                        {sentiment_distribution.map((entry) => (
                          <Cell key={entry.sentiment} fill={SENTIMENT_COLORS[entry.sentiment] || '#9e9e9e'} />
                        ))}
                      </Pie>
                      <Tooltip content={<ChartTip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <Stack direction="row" flexWrap="wrap" gap={1} justifyContent="center" mt={0.5}>
                    {sentiment_distribution.map((s) => (
                      <Stack key={s.sentiment} direction="row" alignItems="center" spacing={0.5}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SENTIMENT_COLORS[s.sentiment] || '#9e9e9e', flexShrink: 0 }} />
                        <Typography variant="caption" color="text.secondary">
                          {s.sentiment.charAt(0).toUpperCase() + s.sentiment.slice(1)}&nbsp;
                          <strong style={{ color: INK[900] }}>({s.count})</strong>
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </>
              )}
            </Panel>
          </motion.div>
        </Grid>

        <Grid item xs={12} md={3}>
          <motion.div variants={fadeUp} style={{ height: '100%' }}>
            <Panel title="Engagement quality" subtitle="Email interaction depth" accent="#C68A2E">
              {engagement_quality.length === 0 ? (
                <Box sx={{ py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Appears as inbound emails are classified.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ pt: 0.5 }}>
                  {engagement_quality.map((e) => {
                    const maxQ = Math.max(...engagement_quality.map((x) => safeNum(x.count)), 1);
                    return (
                      <HBar
                        key={e.quality}
                        label={e.quality.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        value={safeNum(e.count)}
                        max={maxQ}
                        color={QUALITY_COLORS[e.quality] || '#9e9e9e'}
                        count={safeNum(e.count)}
                        isReady={inView}
                      />
                    );
                  })}
                </Box>
              )}
            </Panel>
          </motion.div>
        </Grid>

        <Grid item xs={12} md={3}>
          <motion.div variants={fadeUp} style={{ height: '100%' }}>
            <Panel title="Audience health" subtitle="Derived from status + sentiment signals" accent={G[500]}>
              <Stack spacing={2} sx={{ pt: 0.5 }}>
                {/* Hot ratio */}
                <Box>
                  <Stack direction="row" justifyContent="space-between" mb={0.5}>
                    <Typography variant="caption" fontWeight={500} color={INK[700]}>Hot + urgent ratio</Typography>
                    <Typography variant="caption" fontWeight={700} color="#C4624A">
                      {total > 0 ? ((hotCount / total) * 100).toFixed(1) : 0}%
                    </Typography>
                  </Stack>
                  <Box sx={{ height: 8, borderRadius: 99, bgcolor: INK[100], overflow: 'hidden' }}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: inView && total > 0 ? `${(hotCount / total) * 100}%` : 0 }}
                      transition={{ duration: 0.8, ease: 'easeOut', delay: 0.1 }}
                      style={{ height: '100%', borderRadius: 99, background: '#C4624A' }}
                    />
                  </Box>
                </Box>

                {/* Warm ratio */}
                <Box>
                  <Stack direction="row" justifyContent="space-between" mb={0.5}>
                    <Typography variant="caption" fontWeight={500} color={INK[700]}>Warm ratio</Typography>
                    <Typography variant="caption" fontWeight={700} color="#C68A2E">
                      {total > 0 ? ((warmCount / total) * 100).toFixed(1) : 0}%
                    </Typography>
                  </Stack>
                  <Box sx={{ height: 8, borderRadius: 99, bgcolor: INK[100], overflow: 'hidden' }}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: inView && total > 0 ? `${(warmCount / total) * 100}%` : 0 }}
                      transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
                      style={{ height: '100%', borderRadius: 99, background: '#C68A2E' }}
                    />
                  </Box>
                </Box>

                {/* Conversion rate */}
                <Box>
                  <Stack direction="row" justifyContent="space-between" mb={0.5}>
                    <Typography variant="caption" fontWeight={500} color={INK[700]}>Conversion rate</Typography>
                    <Typography variant="caption" fontWeight={700} color={G[500]}>
                      {conversionRate}%
                    </Typography>
                  </Stack>
                  <Box sx={{ height: 8, borderRadius: 99, bgcolor: INK[100], overflow: 'hidden' }}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: inView ? `${conversionRate}%` : 0 }}
                      transition={{ duration: 0.8, ease: 'easeOut', delay: 0.3 }}
                      style={{ height: '100%', borderRadius: 99, background: G[500] }}
                    />
                  </Box>
                </Box>

                {/* Engagement health */}
                {engagementHealth !== null && (
                  <Box>
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography variant="caption" fontWeight={500} color={INK[700]}>Sentiment health</Typography>
                      <Typography variant="caption" fontWeight={700} color="#4A6B8E">
                        {Math.max(0, engagementHealth)}%
                      </Typography>
                    </Stack>
                    <Box sx={{ height: 8, borderRadius: 99, bgcolor: INK[100], overflow: 'hidden' }}>
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: inView ? `${Math.max(0, engagementHealth)}%` : 0 }}
                        transition={{ duration: 0.8, ease: 'easeOut', delay: 0.4 }}
                        style={{ height: '100%', borderRadius: 99, background: '#4A6B8E' }}
                      />
                    </Box>
                  </Box>
                )}

                <Divider sx={{ borderColor: INK[100] }} />
                <Box sx={{ bgcolor: G[50], borderRadius: 2, px: 2, py: 1.5, border: `1px solid ${G[100]}` }}>
                  <Typography variant="caption" color="text.secondary">Avg buying readiness</Typography>
                  <Typography sx={{ fontFamily: APP_FONT_FAMILY, fontSize: 26, fontWeight: 500, color: G[700], lineHeight: 1 }}>
                    {avgReady}<span style={{ fontSize: 14, color: INK[400] }}>/100</span>
                  </Typography>
                </Box>
              </Stack>
            </Panel>
          </motion.div>
        </Grid>

        {/* ── Inbox activity ── */}
        <Grid item xs={12} md={3}>
          <motion.div variants={fadeUp} style={{ height: '100%' }}>
            <Panel title="Inbox activity" subtitle="Inbound emails classified — last 14 days" accent="#4A6B8E">
              {(() => {
                const total14d = weekly_classifications.reduce((a, d) => a + safeNum(d.count), 0);
                const avgDay   = total14d > 0 ? Math.round(total14d / 14) : 0;
                const peakDay  = weekly_classifications.reduce(
                  (best, d) => safeNum(d.count) > safeNum(best.count) ? d : best,
                  { date: '—', count: 0 },
                );
                const totalEngage   = engagement_quality.reduce((a, e) => a + safeNum(e.count), 0);
                const highCount     = safeNum(engagement_quality.find((e) => e.quality === 'high')?.count);
                const noiseCount    = safeNum(engagement_quality.find((e) => e.quality === 'noise')?.count)
                                    + safeNum(engagement_quality.find((e) => e.quality === 'spam_like')?.count);
                const qualityRate   = totalEngage > 0 ? Math.round((highCount / totalEngage) * 100) : 0;
                const noiseRate     = totalEngage > 0 ? Math.round((noiseCount / totalEngage) * 100) : 0;
                const sparkData     = weekly_classifications.slice(-7).map((d) => ({
                  label: d.date ? d.date.slice(5) : '',
                  count: safeNum(d.count),
                }));

                return (
                  <Stack spacing={0} sx={{ pt: 0.5 }}>
                    {/* Volume row */}
                    <Stack direction="row" spacing={1} mb={1.5}>
                      <Box sx={{ flex: 1, bgcolor: '#EDF2F7', borderRadius: 2, px: 1.5, py: 1, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#4A6B8E', lineHeight: 1 }}>
                          {total14d.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">14-day total</Typography>
                      </Box>
                      <Box sx={{ flex: 1, bgcolor: INK[100], borderRadius: 2, px: 1.5, py: 1, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 22, fontWeight: 700, color: INK[700], lineHeight: 1 }}>
                          {avgDay}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">daily avg</Typography>
                      </Box>
                    </Stack>

                    <Divider sx={{ borderColor: INK[100], mb: 1.25 }} />

                    {/* Quality rows */}
                    {[
                      { label: 'High quality', value: qualityRate, color: G[500] },
                      { label: 'Noise / spam',  value: noiseRate,   color: '#C4624A' },
                    ].map(({ label, value, color }) => (
                      <Box key={label} sx={{ mb: 1 }}>
                        <Stack direction="row" justifyContent="space-between" mb={0.4}>
                          <Typography variant="caption" color={INK[700]} fontWeight={500}>{label}</Typography>
                          <Typography variant="caption" fontWeight={700} color={color}>{value}%</Typography>
                        </Stack>
                        <Box sx={{ height: 5, borderRadius: 99, bgcolor: INK[100], overflow: 'hidden' }}>
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: inView ? `${value}%` : 0 }}
                            transition={{ duration: 0.8, ease: 'easeOut' }}
                            style={{ height: '100%', borderRadius: 99, background: color }}
                          />
                        </Box>
                      </Box>
                    ))}

                    <Divider sx={{ borderColor: INK[100], my: 1 }} />

                    {/* Mini sparkline — last 7 days */}
                    {sparkData.length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Last 7 days
                        </Typography>
                        <ResponsiveContainer width="100%" height={44}>
                          <BarChart data={sparkData} barSize={8} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                            <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                              {sparkData.map((entry, i) => (
                                <Cell
                                  key={i}
                                  fill={entry.count === peakDay.count ? '#4A6B8E' : INK[200]}
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    )}

                    {peakDay.count > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        Peak day: <strong style={{ color: '#4A6B8E' }}>{peakDay.count} emails</strong>
                        {peakDay.date && peakDay.date !== '—' ? ` on ${peakDay.date.slice(5)}` : ''}
                      </Typography>
                    )}
                  </Stack>
                );
              })()}
            </Panel>
          </motion.div>
        </Grid>
      </Grid>

      {/* ── Row 3: Campaign sends | Response outcomes | AI suggestions | Audience reach ── */}
      <Grid container spacing={2} mb={2}>
        {/* Card 1 — Campaign sends */}
        <Grid item xs={12} md={3}>
          <motion.div variants={fadeUp} style={{ height: '100%' }}>
            <Panel title="Campaign sends" subtitle="Total emails sent to contacts" accent={G[500]}>
              {(() => {
                const { total_sends = 0, total_responses = 0, response_rate = 0 } = campaign_performance;
                return (
                  <Stack spacing={1.5} sx={{ pt: 0.5 }}>
                    <Box sx={{ bgcolor: G[50], borderRadius: 2, px: 1.5, py: 1.5, border: `1px solid ${G[100]}`, textAlign: 'center' }}>
                      <Typography sx={{ fontFamily: APP_FONT_FAMILY, fontSize: 30, fontWeight: 500, color: G[700], lineHeight: 1 }}>
                        {safeNum(total_sends).toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">emails sent</Typography>
                    </Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="text.secondary">{safeNum(total_responses).toLocaleString()} responses</Typography>
                      <Typography variant="caption" fontWeight={700} color={G[500]}>{response_rate}%</Typography>
                    </Stack>
                    <Box sx={{ height: 6, borderRadius: 99, bgcolor: INK[100], overflow: 'hidden' }}>
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: inView ? `${Math.min(safeNum(response_rate), 100)}%` : 0 }}
                        transition={{ duration: 0.8, ease: 'easeOut' }}
                        style={{ height: '100%', borderRadius: 99, background: `linear-gradient(90deg, ${G[700]}, ${G[300]})` }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">response rate</Typography>
                  </Stack>
                );
              })()}
            </Panel>
          </motion.div>
        </Grid>

        {/* Card 2 — Response outcomes */}
        <Grid item xs={12} md={3}>
          <motion.div variants={fadeUp} style={{ height: '100%' }}>
            <Panel title="Response outcomes" subtitle="How contacts reacted to campaigns" accent={G[300]}>
              {(() => {
                const { responders = 0, non_responders = 0, never_sent = 0 } = campaign_performance;
                return (
                  <Stack spacing={1} sx={{ pt: 0.5 }}>
                    {[
                      { label: 'Responded',  value: responders,     color: G[500],    bg: G[50] },
                      { label: 'No reply',   value: non_responders, color: '#C68A2E', bg: '#FBF4E8' },
                      { label: 'Never sent', value: never_sent,     color: INK[400],  bg: INK[100] },
                    ].map(({ label, value, color, bg }) => (
                      <Box key={label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: bg, borderRadius: 2, px: 1.5, py: 1 }}>
                        <Typography variant="caption" fontWeight={500} color={INK[700]}>{label}</Typography>
                        <Typography sx={{ fontSize: 18, fontWeight: 700, color, lineHeight: 1 }}>
                          {safeNum(value).toLocaleString()}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                );
              })()}
            </Panel>
          </motion.div>
        </Grid>

        {/* Card 3 — AI suggestions */}
        <Grid item xs={12} md={3}>
          <motion.div variants={fadeUp} style={{ height: '100%' }}>
            <Panel title="AI suggestions" subtitle="Weekly audience suggestions — last 4 weeks" accent="#4A6B8E">
              {(() => {
                const { total = 0, pending = 0, approved = 0, rejected = 0 } = weekly_plan_summary;
                return (
                  <Stack spacing={1.5} sx={{ pt: 0.5 }}>
                    {safeNum(pending) > 0 ? (
                      <Box sx={{ bgcolor: '#FBF4E8', borderRadius: 2, px: 1.5, py: 1, border: '1px solid #F6E0B5', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#C68A2E', flexShrink: 0 }} />
                        <Typography variant="caption" fontWeight={600} color="#C68A2E">
                          {pending} need review
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ bgcolor: G[50], borderRadius: 2, px: 1.5, py: 1, border: `1px solid ${G[100]}`, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: G[500], flexShrink: 0 }} />
                        <Typography variant="caption" fontWeight={600} color={G[500]}>All reviewed</Typography>
                      </Box>
                    )}
                    <Stack spacing={1}>
                      {[
                        { label: 'Pending',  value: pending,  color: '#C68A2E', bg: '#FBF4E8' },
                        { label: 'Approved', value: approved, color: G[500],    bg: G[50] },
                        { label: 'Rejected', value: rejected, color: INK[400],  bg: INK[100] },
                      ].map(({ label, value, color, bg }) => (
                        <Box key={label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: bg, borderRadius: 2, px: 1.5, py: 1 }}>
                          <Typography variant="caption" fontWeight={500} color={INK[700]}>{label}</Typography>
                          <Typography sx={{ fontSize: 18, fontWeight: 700, color, lineHeight: 1 }}>{safeNum(value)}</Typography>
                        </Box>
                      ))}
                    </Stack>
                    {safeNum(total) === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        No suggestions yet — runs each weekend.
                      </Typography>
                    )}
                  </Stack>
                );
              })()}
            </Panel>
          </motion.div>
        </Grid>

        {/* Card 4 — Audience reach */}
        <Grid item xs={12} md={3}>
          <motion.div variants={fadeUp} style={{ height: '100%' }}>
            <Panel title="Audience reach" subtitle="Approved segment size & AI confidence" accent="#4A6B8E">
              {(() => {
                const { total_reach = 0, avg_confidence = 0 } = weekly_plan_summary;
                return (
                  <Stack spacing={1.5} sx={{ pt: 0.5 }}>
                    <Box sx={{ bgcolor: '#EDF2F7', borderRadius: 2, px: 1.5, py: 1.5, border: '1px solid #BEE3F8', textAlign: 'center' }}>
                      <Typography sx={{ fontFamily: APP_FONT_FAMILY, fontSize: 30, fontWeight: 500, color: '#4A6B8E', lineHeight: 1 }}>
                        {safeNum(total_reach).toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">contacts reachable</Typography>
                    </Box>
                    <Box>
                      <Stack direction="row" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" fontWeight={500} color={INK[700]}>Avg confidence</Typography>
                        <Typography variant="caption" fontWeight={700} color={G[500]}>{safeNum(avg_confidence)}%</Typography>
                      </Stack>
                      <Box sx={{ height: 6, borderRadius: 99, bgcolor: INK[100], overflow: 'hidden' }}>
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: inView ? `${Math.min(safeNum(avg_confidence), 100)}%` : 0 }}
                          transition={{ duration: 0.8, ease: 'easeOut', delay: 0.15 }}
                          style={{ height: '100%', borderRadius: 99, background: `linear-gradient(90deg, ${G[700]}, ${G[300]})` }}
                        />
                      </Box>
                    </Box>
                  </Stack>
                );
              })()}
            </Panel>
          </motion.div>
        </Grid>
      </Grid>

      {/* ── Full-width: Classification activity area chart ── */}
      <motion.div variants={fadeUp}>
        <Panel
          title="Classification activity (last 14 days)"
          subtitle="New inbound emails classified by the AI pipeline each day"
          accent="#4A6B8E"
        >
          {weekly_classifications.every((d) => safeNum(d.count) === 0) ? (
            <Box sx={{ py: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No classifications in the last 14 days. Run the backfill command to populate historical data.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={areaData} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="classifyGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={G[500]} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={G[500]} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={INK[100]} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: INK[400] }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: INK[400] }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<ChartTip />} />
                <Area
                  type="monotone"
                  dataKey="count"
                  name="Classified"
                  stroke={G[500]}
                  strokeWidth={2.5}
                  fill="url(#classifyGrad)"
                  dot={{ r: 4, fill: G[500], strokeWidth: 0 }}
                  activeDot={{ r: 6, fill: G[700], strokeWidth: 0 }}
                  isAnimationActive
                  animationBegin={300}
                  animationDuration={1000}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </motion.div>

    </motion.div>
  );
}
